import { create } from "zustand";
import type { Role } from "@mypos/ui";
import { idb } from "./idb";
import { api, endSession, onAuthCleared } from "./api";

export type AuthUser = {
  id: string;
  name: string;
  email: string | null;
  role: Role;
  storeId: string | null;
  permissions: string[];
  // Forced-password-change flag from the API (bootstrap admin / admin reset).
  // When true, App routes to the ChangePassword gate before the shell renders.
  mustChangePassword?: boolean;
};

type State = {
  user: AuthUser | null;
  loading: boolean;
  error: string | null;
  hydrate: () => Promise<void>;
  loginEmail: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  can: (key: string) => boolean;
};

async function storeTokens(accessToken: string, refreshToken: string) {
  await idb.set("auth.accessToken", accessToken);
  await idb.set("auth.refreshToken", refreshToken);
}

let authClearedSubscribed = false;

export const useAuthStore = create<State>((set, get) => ({
  user: null,
  loading: true,
  error: null,

  async hydrate() {
    // Subscribe once to api.ts auth-cleared events. Fires when /auth/refresh
    // is rejected — without this the store keeps showing the user as logged
    // in while every API call returns Unauthorized.
    if (!authClearedSubscribed) {
      authClearedSubscribed = true;
      onAuthCleared(() => set({ user: null, error: null }));
    }
    const token = await idb.get<string>("auth.accessToken");
    if (!token) {
      set({ loading: false });
      return;
    }
    try {
      const me = await api.me();
      const user: AuthUser = {
        id: me.id,
        name: me.name,
        email: me.email,
        role: me.roleName as Role,
        storeId: me.storeId,
        permissions: me.permissions ?? [],
        mustChangePassword: me.mustChangePassword ?? false,
      };
      await idb.set("user", user);
      set({ user, loading: false, error: null });
    } catch {
      await idb.remove("auth.accessToken");
      await idb.remove("auth.refreshToken");
      await idb.remove("user");
      set({ user: null, loading: false });
    }
  },

  async loginEmail(email, password) {
    set({ loading: true, error: null });
    try {
      const res = await api.loginPassword(email, password);
      await storeTokens(res.accessToken, res.refreshToken);
      await get().hydrate();
    } catch (e) {
      set({ error: e instanceof Error ? e.message : String(e), loading: false });
    }
  },

  async logout() {
    // Route to the login screen immediately and unconditionally — the visual
    // sign-out must never wait on IndexedDB or the network. endSession()
    // then fences in-flight token refreshes (which used to silently
    // resurrect the session), clears storage, and revokes server-side.
    set({ user: null });
    try {
      await endSession();
    } catch {
      /* storage failure — user state is already cleared for this tab */
    }
  },

  can(key) {
    const u = get().user;
    return !!u && (u.role === "manager" || u.permissions.includes(key));
  },
}));
